import { LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useServerStockAccountValidation } from '../hooks/useServerStockAccountValidation';
import { useUpsertStockAccountForm } from '../hooks/useUpsertStockAccountForm';
import type { UpsertStockAccountDto } from '../types/StockAccountDto';
import { UpsertStockAccountForm } from './UpsertStockAccountForm';

interface NewStockAccountModalProps {
	close: () => void;
	opened: boolean;
	triggerListUpdate: () => void;
}

export function NewStockAccountModal({
	opened,
	close,
	triggerListUpdate,
}: NewStockAccountModalProps) {
	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertStockAccountForm();

	const newAccountMutation = mutate<UpsertStockAccountDto>(
		'stock/accounts/add',
		inputs,
	);

	useServerStockAccountValidation(newAccountMutation, form);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		triggerListUpdate();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={resetAndClose}
				title={<Title label={'New Stock Account'} />}
				w={100}
				withCloseButton={true}
				size={'lg'}
				centered
			>
				<LoadingOverlay visible={newAccountMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Account Added!'}
					body={
						'A new stock account has been successfully added. Click Ok to continue...'
					}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newAccountMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertStockAccountForm
						form={form}
						organisationId={inputs.organisationId}
						isEdit={inputs.isEdit}
					/>
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
